import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertTriangle,
  Shield,
  Plus,
  Edit,
  Eye,
  MoreVertical,
  X,
  Search,
  Filter,
  Star,
  CheckCircle2,
  AlertCircle,
  Settings,
  Zap,
  FlaskConical,
} from "lucide-react";
import type { RelatedRiskEntry } from "../../_types/assignment-types";

interface RisksControlsTabProps {
  assignmentId: string;
  relatedRisksData: RelatedRiskEntry[];
  showAddSection: boolean;
  isAddingRiskControl: boolean;
  loadingRisks: boolean;
  loadingControls: boolean;
  availableRisks: any[];
  availableControls: any[];
  selectedRiskIds: Set<string>;
  selectedControlIds: Set<string>;
  riskSearchTerm: string;
  controlSearchTerm: string;
  selectedRiskLevelFilter: string;
  selectedControlTypeFilter: string;
  selectedRiskCategoryFilter: string;
  selectedControlEffectivenessFilter: string;
  showOnlyUnlinked: boolean;
  quickFilterMode: "all" | "critical" | "recommended";
  onAddRiskControlClick: () => void;
  onCloseAddSection: () => void;
  onSetSelectedRiskIds: (ids: Set<string>) => void;
  onSetSelectedControlIds: (ids: Set<string>) => void;
  onSetRiskSearchTerm: (term: string) => void;
  onSetControlSearchTerm: (term: string) => void;
  onSetSelectedRiskLevelFilter: (filter: string) => void;
  onSetSelectedControlTypeFilter: (filter: string) => void;
  onSetSelectedRiskCategoryFilter: (filter: string) => void;
  onSetSelectedControlEffectivenessFilter: (filter: string) => void;
  onSetShowOnlyUnlinked: (show: boolean) => void;
  onSetQuickFilterMode: (mode: "all" | "critical" | "recommended") => void;
  onRemoveRisk: (riskId: string) => void;
  onRemoveControl: (riskId: string, controlId: string) => void;
  onTestControl: (control: { id: string; name: string }) => void;
  onViewTestDetails: (testDetails: any) => void;
  onEditTest: (control: { id: string; name: string }, testDetails: any) => void;
  onSaveRiskControls: () => Promise<void>;
  getFilteredRisks: () => any[];
  getFilteredControls: () => any[];
  getRiskLevels: () => string[];
  getControlTypes: () => string[];
  getRiskCategories: () => string[];
  getControlEffectiveness: () => string[];
  getRiskColor: (risk: string) => string;
  toast: (options: {
    title: string;
    description?: string;
    variant?: "default" | "destructive" | null;
  }) => void;
}

export function RisksControlsTab({
  assignmentId,
  relatedRisksData,
  showAddSection,
  isAddingRiskControl,
  loadingRisks,
  loadingControls,
  availableRisks,
  availableControls,
  selectedRiskIds,
  selectedControlIds,
  riskSearchTerm,
  controlSearchTerm,
  selectedRiskLevelFilter,
  selectedControlTypeFilter,
  selectedRiskCategoryFilter,
  selectedControlEffectivenessFilter,
  showOnlyUnlinked,
  quickFilterMode,
  onAddRiskControlClick,
  onCloseAddSection,
  onSetSelectedRiskIds,
  onSetSelectedControlIds,
  onSetRiskSearchTerm,
  onSetControlSearchTerm,
  onSetSelectedRiskLevelFilter,
  onSetSelectedControlTypeFilter,
  onSetSelectedRiskCategoryFilter,
  onSetSelectedControlEffectivenessFilter,
  onSetShowOnlyUnlinked,
  onSetQuickFilterMode,
  onRemoveRisk,
  onRemoveControl,
  onTestControl,
  onViewTestDetails,
  onEditTest,
  onSaveRiskControls,
  getFilteredRisks,
  getFilteredControls,
  getRiskLevels,
  getControlTypes,
  getRiskCategories,
  getControlEffectiveness,
  getRiskColor,
  toast,
}: RisksControlsTabProps) {
  const hasActiveFilters =
    riskSearchTerm ||
    controlSearchTerm ||
    selectedRiskLevelFilter !== "All" ||
    selectedControlTypeFilter !== "All" ||
    selectedRiskCategoryFilter !== "All" ||
    selectedControlEffectivenessFilter !== "All" ||
    showOnlyUnlinked ||
    quickFilterMode !== "all";

  const handleSaveClick = async () => {
    const totalSelected = selectedRiskIds.size + selectedControlIds.size;
    if (totalSelected === 0) {
      toast({
        title: "No Selection",
        description: "Please select at least one risk or control to add.",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await fetch(`/api/assignments/${assignmentId}/add-risk-controls`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          riskIds: Array.from(selectedRiskIds),
          controlIds: Array.from(selectedControlIds),
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save: ${response.status}`);
      }

      await response.json();
      await onSaveRiskControls();

      toast({
        title: "Success",
        description: `Successfully linked ${selectedRiskIds.size} risks and ${selectedControlIds.size} controls to this assignment!`,
      });

      onCloseAddSection();
    } catch (error) {
      console.error("Error saving risk-control relationships:", error);
      toast({
        title: "Error",
        description: "Failed to save risk-control relationships. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Risks & Controls
            </CardTitle>
            <CardDescription>Risk assessment and control effectiveness</CardDescription>
          </div>
          <Button
            onClick={onAddRiskControlClick}
            disabled={isAddingRiskControl}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Risk & Control
          </Button>
        </div>
      </CardHeader>

      {/* Expandable Add Risks & Controls Section */}
      {showAddSection && (
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                Add Risks & Controls
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Select items from the library to link to this assignment
                {hasActiveFilters && (
                  <span className="text-blue-600 font-medium ml-2">• Filters active</span>
                )}
              </p>
            </div>

            {/* Quick Filter Modes */}
            <div className="flex gap-2">
              <Button
                variant={quickFilterMode === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => onSetQuickFilterMode("all")}
                className="text-xs"
              >
                <Settings className="h-3 w-3 mr-1" />
                All Items
              </Button>
              <Button
                variant={quickFilterMode === "critical" ? "default" : "outline"}
                size="sm"
                onClick={() => onSetQuickFilterMode("critical")}
                className="text-xs"
              >
                <AlertCircle className="h-3 w-3 mr-1" />
                Critical
              </Button>
              <Button
                variant={quickFilterMode === "recommended" ? "default" : "outline"}
                size="sm"
                onClick={() => onSetQuickFilterMode("recommended")}
                className="text-xs"
              >
                <Star className="h-3 w-3 mr-1" />
                Recommended
              </Button>
            </div>
          </div>

          {/* Global Controls */}
          <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filter Options
              </h4>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={showOnlyUnlinked}
                    onChange={(e) => onSetShowOnlyUnlinked(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  Show only unlinked items
                </label>
              </div>
            </div>
          </div>

          {/* Overall loading indicator */}
          {(loadingRisks || loadingControls) && (
            <div className="flex items-center justify-center py-4 mb-4 bg-blue-50 rounded-lg">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm text-blue-600">Loading data...</span>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Available Risks Column */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-orange-50 px-4 py-3 border-b border-orange-100">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  Available Risks
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {getFilteredRisks().length} of {availableRisks.length}
                  </Badge>
                </h4>
              </div>

              {/* Risk Search & Filters */}
              <div className="p-4 space-y-3 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by title or description..."
                    value={riskSearchTerm}
                    onChange={(e) => onSetRiskSearchTerm(e.target.value)}
                    className="pl-10 h-9 text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">
                      Risk Level:
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {getRiskLevels().map((level) => (
                        <Button
                          key={level}
                          variant={
                            selectedRiskLevelFilter === level ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => onSetSelectedRiskLevelFilter(level)}
                          className="h-6 px-2 text-xs"
                        >
                          {level}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">
                      Category:
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {getRiskCategories().map((category) => (
                        <Button
                          key={category}
                          variant={
                            selectedRiskCategoryFilter === category
                              ? "default"
                              : "outline"
                          }
                          size="sm"
                          onClick={() => onSetSelectedRiskCategoryFilter(category)}
                          className="h-6 px-2 text-xs"
                        >
                          {category}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Risks List */}
              <div className="max-h-80 overflow-y-auto">
                {loadingRisks ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto mb-2"></div>
                    Loading risks...
                  </div>
                ) : getFilteredRisks().length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p className="font-medium">
                      {availableRisks.length === 0
                        ? "No risks available"
                        : "No risks match your filters"}
                    </p>
                    {availableRisks.length > 0 && (
                      <p className="text-xs mt-1">
                        Try adjusting your filters or search terms
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {getFilteredRisks().map((risk) => {
                      const isSelected = selectedRiskIds.has(risk.id.toString());
                      const isLinked = relatedRisksData.some(
                        (riskEntry) =>
                          riskEntry.risk.id === risk.id ||
                          riskEntry.risk.id === risk.id.toString(),
                      );
                      return (
                        <label
                          key={risk.id}
                          className={`flex items-start gap-3 p-4 hover:bg-gray-50 cursor-pointer transition-colors ${isSelected ? "bg-blue-50 border-l-4 border-blue-500" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              const newSelected = new Set(selectedRiskIds);
                              if (e.target.checked) {
                                newSelected.add(risk.id.toString());
                              } else {
                                newSelected.delete(risk.id.toString());
                              }
                              onSetSelectedRiskIds(newSelected);
                            }}
                            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <p className="font-medium text-sm text-gray-900">
                                {risk.title}
                              </p>
                              {isLinked && (
                                <CheckCircle2 className="h-4 w-4 text-green-500 ml-2 flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                              {risk.description}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge
                                variant="outline"
                                className={`text-xs ${
                                  (
                                    risk.riskLevel ||
                                    risk.inherentRisk ||
                                    "Medium"
                                  ).toLowerCase() === "critical"
                                    ? "border-red-500 text-red-700 bg-red-50"
                                    : (
                                          risk.riskLevel ||
                                          risk.inherentRisk ||
                                          "Medium"
                                        ).toLowerCase() === "high"
                                      ? "border-orange-500 text-orange-700 bg-orange-50"
                                      : (
                                            risk.riskLevel ||
                                            risk.inherentRisk ||
                                            "Medium"
                                          ).toLowerCase() === "medium"
                                        ? "border-yellow-500 text-yellow-700 bg-yellow-50"
                                        : "border-green-500 text-green-700 bg-green-50"
                                }`}
                              >
                                {risk.riskLevel || risk.inherentRisk || "Medium"}
                              </Badge>
                              {(risk.process || risk.category) && (
                                <Badge variant="secondary" className="text-xs">
                                  {risk.process || risk.category}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Available Controls Column */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-green-50 px-4 py-3 border-b border-green-100">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-green-500" />
                  Available Controls
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {getFilteredControls().length} of {availableControls.length}
                  </Badge>
                </h4>
              </div>

              {/* Control Search & Filters */}
              <div className="p-4 space-y-3 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by name or description..."
                    value={controlSearchTerm}
                    onChange={(e) => onSetControlSearchTerm(e.target.value)}
                    className="pl-10 h-9 text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">
                      Control Type:
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {getControlTypes().map((type) => (
                        <Button
                          key={type}
                          variant={
                            selectedControlTypeFilter === type ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => onSetSelectedControlTypeFilter(type)}
                          className="h-6 px-2 text-xs"
                        >
                          {type}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">
                      Effectiveness:
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {getControlEffectiveness().map((effectiveness) => (
                        <Button
                          key={effectiveness}
                          variant={
                            selectedControlEffectivenessFilter === effectiveness
                              ? "default"
                              : "outline"
                          }
                          size="sm"
                          onClick={() =>
                            onSetSelectedControlEffectivenessFilter(effectiveness)
                          }
                          className="h-6 px-2 text-xs"
                        >
                          {effectiveness}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls List */}
              <div className="max-h-80 overflow-y-auto">
                {loadingControls ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500 mx-auto mb-2"></div>
                    Loading controls...
                  </div>
                ) : getFilteredControls().length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Shield className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p className="font-medium">
                      {availableControls.length === 0
                        ? "No controls available"
                        : "No controls match your filters"}
                    </p>
                    {availableControls.length > 0 && (
                      <p className="text-xs mt-1">
                        Try adjusting your filters or search terms
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {getFilteredControls().map((control) => {
                      const isSelected = selectedControlIds.has(control.id.toString());
                      const isLinked = relatedRisksData.some((riskEntry) =>
                        riskEntry.controls.some(
                          (ctrl) =>
                            ctrl.id === control.id || ctrl.id === control.id.toString(),
                        ),
                      );
                      return (
                        <label
                          key={control.id}
                          className={`flex items-start gap-3 p-4 hover:bg-gray-50 cursor-pointer transition-colors ${isSelected ? "bg-blue-50 border-l-4 border-blue-500" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              const newSelected = new Set(selectedControlIds);
                              if (e.target.checked) {
                                newSelected.add(control.id.toString());
                              } else {
                                newSelected.delete(control.id.toString());
                              }
                              onSetSelectedControlIds(newSelected);
                            }}
                            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <p className="font-medium text-sm text-gray-900">
                                {control.name}
                              </p>
                              {isLinked && (
                                <CheckCircle2 className="h-4 w-4 text-green-500 ml-2 flex-shrink-0" />
                              )}
                            </div>
                            {control.description && (
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                {control.description}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              <Badge
                                variant="secondary"
                                className={`text-xs ${
                                  (control.type || control.typeName || "")
                                    .toLowerCase()
                                    .includes("preventive")
                                    ? "bg-blue-100 text-blue-700"
                                    : (control.type || control.typeName || "")
                                          .toLowerCase()
                                          .includes("detective")
                                      ? "bg-purple-100 text-purple-700"
                                      : (control.type || control.typeName || "")
                                            .toLowerCase()
                                            .includes("corrective")
                                        ? "bg-orange-100 text-orange-700"
                                        : "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {control.type || control.typeName || "Control"}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={`text-xs ${
                                  (control.effectiveness || "")
                                    .toLowerCase()
                                    .includes("effective")
                                    ? "border-green-500 text-green-700 bg-green-50"
                                    : (control.effectiveness || "")
                                          .toLowerCase()
                                          .includes("adequate")
                                      ? "border-yellow-500 text-yellow-700 bg-yellow-50"
                                      : "border-gray-500 text-gray-700 bg-gray-50"
                                }`}
                              >
                                {control.effectiveness || "Effective"}
                              </Badge>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white p-4 border-t border-gray-200 rounded-b-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                  {selectedRiskIds.size} risks selected
                </span>
                <span className="flex items-center gap-1">
                  <Shield className="h-4 w-4 text-green-500" />
                  {selectedControlIds.size} controls selected
                </span>
                {(selectedRiskIds.size > 0 || selectedControlIds.size > 0) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      onSetSelectedRiskIds(new Set());
                      onSetSelectedControlIds(new Set());
                    }}
                    className="text-xs h-6 px-2"
                  >
                    Clear All
                  </Button>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={onCloseAddSection}
                  className="min-w-20"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveClick}
                  disabled={selectedRiskIds.size + selectedControlIds.size === 0}
                  className="bg-blue-600 hover:bg-blue-700 text-white min-w-32 flex items-center gap-2"
                >
                  <Zap className="h-4 w-4" />
                  Add Selected Items ({selectedRiskIds.size + selectedControlIds.size})
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CardContent>
        <div className="space-y-6">
          {relatedRisksData.map((riskEntry) => (
            <div key={riskEntry.risk.id} className="border rounded-lg p-4">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="font-semibold text-lg">{riskEntry.risk.title}</h4>
                  <p className="text-muted-foreground mt-1">
                    {riskEntry.risk.description}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    className={getRiskColor(
                      (riskEntry.risk as any).riskLevel || riskEntry.risk.inherentRisk,
                    )}
                  >
                    {(riskEntry.risk as any).riskLevel || riskEntry.risk.inherentRisk}{" "}
                    Risk
                  </Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => onRemoveRisk(riskEntry.risk.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Remove Risk
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="space-y-3">
                <h5 className="font-medium">Controls</h5>
                {riskEntry.controls.map((control, controlIndex) => (
                  <div
                    key={`${riskEntry.risk.id}-${control.id}-${controlIndex}`}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium">{control.name}</p>
                        {(control as any).latestTestResult && (
                          <Badge
                            variant="outline"
                            className={`text-xs h-5 ${
                              (control as any).latestTestResult === "Passed"
                                ? "bg-green-50 text-green-700 border-green-200"
                                : (control as any).latestTestResult === "Failed"
                                  ? "bg-red-50 text-red-700 border-red-200"
                                  : (control as any).latestTestResult ===
                                      "Passed with Exceptions"
                                    ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                                    : "bg-gray-50 text-gray-700 border-gray-200"
                            }`}
                          >
                            {(control as any).latestTestResult}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Last assessed:{" "}
                        {(control as any).latestTestDate
                          ? new Date((control as any).latestTestDate).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              },
                            ) +
                            ((control as any).latestTesterName
                              ? ` by ${(control as any).latestTesterName}`
                              : "")
                          : control.lastAssessed || "Never"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={
                          control.assessment === "Effective"
                            ? "bg-green-100 text-green-800 border-green-200"
                            : control.assessment === "Needs Improvement"
                              ? "bg-yellow-100 text-yellow-800 border-yellow-200"
                              : "bg-red-100 text-red-800 border-red-200"
                        }
                      >
                        {control.assessment}
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              onTestControl({
                                id: control.id,
                                name: control.name,
                              });
                            }}
                          >
                            <FlaskConical className="h-4 w-4 mr-2" />
                            Test Control
                          </DropdownMenuItem>
                          {(control as any).latestTestResult && (
                            <>
                              <DropdownMenuItem
                                onClick={() => {
                                  onViewTestDetails({
                                    controlId: control.id,
                                    controlName: control.name,
                                    riskId: riskEntry.risk.id,
                                    testResult: (control as any).latestTestResult,
                                    testDate: (control as any).latestTestDate,
                                    testerName: (control as any).latestTesterName,
                                  });
                                }}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View Test Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  onEditTest(
                                    {
                                      id: control.id,
                                      name: control.name,
                                    },
                                    {
                                      controlId: control.id,
                                      controlName: control.name,
                                      riskId: riskEntry.risk.id,
                                      testResult: (control as any).latestTestResult,
                                      testDate: (control as any).latestTestDate,
                                      testerName: (control as any).latestTesterName,
                                    },
                                  );
                                }}
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Test
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem
                            onClick={() => onRemoveControl(riskEntry.risk.id, control.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="h-4 w-4 mr-2" />
                            Remove Control
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>

              {riskEntry.residualRisk && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Residual Risk:</span>
                    <Badge className={getRiskColor(riskEntry.residualRisk || "Medium")}>
                      {riskEntry.residualRisk || "Medium"}
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
